import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Chip, IconButton, TextField,
  Select, MenuItem, FormControl, InputLabel, Box, Typography,
  InputAdornment, CircularProgress, Card, CardContent,
  Checkbox, FormControlLabel,
  useMediaQuery, useTheme
} from '@mui/material';
import { Add, Visibility, Edit, Delete, Upload, Search, AttachFile } from '@mui/icons-material';
import { ticketService } from '../services/ticketService';
import { kcService } from '../services/kcService';
import { userService } from '../services/userService';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';

const Tickets = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [tickets, setTickets] = useState([]);
  const [kcs, setKcs] = useState([]);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [bulkAssignee, setBulkAssignee] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    kc: '',
    active: !isAdmin,
    unassigned: false,
    assignee: ''
  });
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const observerTarget = useRef(null);
  const loadingRef = useRef(false);
  const offsetRef = useRef(0);
  const LIMIT = 50;

  const loadKcs = async () => {
    try {
      const kcsData = await kcService.getAllKcs();
      setKcs(kcsData);
    } catch (error) {
      console.error('Fehler beim Laden der KCs');
    }
  };

  const loadUsers = async () => {
    try {
      const usersData = await userService.getAllUsers();
      setUsers(usersData);
    } catch (error) {
      console.error('Fehler beim Laden der Benutzer');
    }
  };

  const assignableUsers = users.filter(u => u.role === 'technician' || u.role === 'admin');

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const allVisibleSelected = tickets.length > 0 && tickets.every(t => selected.includes(t.id));
  const toggleSelectAll = () => {
    setSelected(allVisibleSelected ? [] : tickets.map(t => t.id));
  };

  const handleBulkAssign = async () => {
    if (selected.length === 0) return;
    try {
      const res = await ticketService.bulkAssign(selected, bulkAssignee || null);
      toast.success(`${res.updated} Ticket(s) zugewiesen`);
      setSelected([]);
      setBulkAssignee('');
      loadTickets(true);
    } catch (error) {
      toast.error('Sammel-Zuweisung fehlgeschlagen');
    }
  };

  const loadTickets = async (reset = false) => {
    if (loadingRef.current) return;

    try {
      loadingRef.current = true;
      setLoading(true);
      const currentOffset = reset ? 0 : offsetRef.current;
      const data = await ticketService.getAllTickets(filters, LIMIT, currentOffset);

      if (reset) {
        setTickets(data);
        offsetRef.current = LIMIT;
        setOffset(LIMIT);
      } else {
        setTickets(prev => [...prev, ...data]);
        offsetRef.current = offsetRef.current + LIMIT;
        setOffset(prev => prev + LIMIT);
      }

      setHasMore(data.length === LIMIT);
    } catch (error) {
      toast.error('Fehler beim Laden der Tickets');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  useEffect(() => {
    setTickets([]);
    setOffset(0);
    offsetRef.current = 0;
    setHasMore(true);
    setSelected([]);
    loadTickets(true);
  }, [filters]);

  useEffect(() => {
    loadKcs();
    if (isAdmin) loadUsers();
  }, [isAdmin]);

  useEffect(() => {
    if (!hasMore || loadingRef.current) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && !loadingRef.current) {
          loadTickets(false);
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, tickets.length]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => {
      const next = { ...prev, [field]: value };
      // "Nur aktive" und expliziter Statusfilter schließen sich gegenseitig aus
      if (field === 'active' && value) next.status = '';
      if (field === 'status' && value) next.active = false;
      // "Nicht zugewiesen" und Techniker-Filter schließen sich gegenseitig aus
      if (field === 'unassigned' && value) next.assignee = '';
      if (field === 'assignee' && value) next.unassigned = false;
      return next;
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Möchten Sie dieses Ticket wirklich löschen?')) {
      try {
        await ticketService.deleteTicket(id);
        toast.success('Ticket erfolgreich gelöscht');
        loadTickets(true);
      } catch (error) {
        toast.error('Fehler beim Löschen des Tickets');
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'warning',
      in_progress: 'info',
      resolved: 'success',
      closed: 'default',
      cancelled: 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      open: 'Offen',
      in_progress: 'In Bearbeitung',
      resolved: 'Fertig Bearbeitet',
      closed: 'Abgerechnet',
      cancelled: 'Storniert'
    };
    return labels[status] || status;
  };


  return (
    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
        <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2.125rem' }, background: 'linear-gradient(135deg, #CC0000 0%, #8B0000 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Tickets</Typography>
        {isAdmin && (
          <Box display="flex" gap={1}>
            <Button variant="outlined" startIcon={<Upload />} onClick={() => navigate('/tickets/import')} size={isMobile ? 'small' : 'medium'} sx={{ flex: { xs: 1, sm: 'none' } }}>
              Importieren
            </Button>
            <Button variant="outlined" startIcon={<Upload />} onClick={() => navigate('/tickets/import-pptx')} size={isMobile ? 'small' : 'medium'} sx={{ flex: { xs: 1, sm: 'none' }, borderColor: '#CC0000', color: '#CC0000', '&:hover': { borderColor: '#8B0000', backgroundColor: 'rgba(204,0,0,0.04)' } }}>
              PPTX
            </Button>
            <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/tickets/new')} size={isMobile ? 'small' : 'medium'} sx={{ flex: { xs: 1, sm: 'none' } }}>
              Neues Ticket
            </Button>
          </Box>
        )}
      </Box>

      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
        <Box display="flex" flexWrap="wrap" gap={{ xs: 1, sm: 2 }} alignItems="center">
          <TextField size="small" placeholder="Suchen..." value={filters.search} onChange={(e) => handleFilterChange('search', e.target.value)}
            InputProps={{ startAdornment: (<InputAdornment position="start"><Search sx={{ color: 'text.secondary' }} /></InputAdornment>) }}
            sx={{ minWidth: { xs: '100%', sm: 250 } }}
          />
          <FormControl size="small" sx={{ minWidth: { xs: 'calc(50% - 4px)', sm: 120 } }} disabled={filters.active}>
            <InputLabel>Status</InputLabel>
            <Select value={filters.active ? '' : filters.status} label="Status" onChange={(e) => handleFilterChange('status', e.target.value)}>
              <MenuItem value="">Alle</MenuItem>
              <MenuItem value="open">Offen</MenuItem>
              <MenuItem value="in_progress">In Bearbeitung</MenuItem>
              <MenuItem value="resolved">Fertig Bearbeitet</MenuItem>
              <MenuItem value="closed">Abgerechnet</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: 'calc(50% - 4px)', sm: 120 } }}>
            <InputLabel>KC</InputLabel>
            <Select value={filters.kc} label="KC" onChange={(e) => handleFilterChange('kc', e.target.value)}>
              <MenuItem value="">Alle</MenuItem>
              {kcs.map(kc => (<MenuItem key={kc.id} value={kc.id}>{kc.name}</MenuItem>))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Checkbox size="small" checked={!!filters.active} onChange={(e) => handleFilterChange('active', e.target.checked)} />}
            label="Nur aktive"
          />
          {isAdmin && (
            <>
              <FormControl size="small" sx={{ minWidth: { xs: 'calc(50% - 4px)', sm: 160 } }} disabled={filters.unassigned}>
                <InputLabel>Techniker</InputLabel>
                <Select value={filters.unassigned ? '' : filters.assignee} label="Techniker" onChange={(e) => handleFilterChange('assignee', e.target.value)}>
                  <MenuItem value="">Alle</MenuItem>
                  {assignableUsers.map(u => (<MenuItem key={u.id} value={u.id}>{u.username}</MenuItem>))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={<Checkbox size="small" checked={!!filters.unassigned} onChange={(e) => handleFilterChange('unassigned', e.target.checked)} />}
                label="Nicht zugewiesen"
              />
            </>
          )}
        </Box>
      </Paper>

      {isAdmin && selected.length > 0 && (
        <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', backgroundColor: 'rgba(204,0,0,0.04)' }}>
          <Typography sx={{ fontWeight: 600 }}>{selected.length} ausgewählt</Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Zuweisen an</InputLabel>
            <Select value={bulkAssignee} label="Zuweisen an" onChange={(e) => setBulkAssignee(e.target.value)}>
              <MenuItem value="">— nicht zugewiesen —</MenuItem>
              {assignableUsers.map(u => (<MenuItem key={u.id} value={u.id}>{u.username}</MenuItem>))}
            </Select>
          </FormControl>
          <Button variant="contained" onClick={handleBulkAssign}>Zuweisen</Button>
          <Button onClick={() => setSelected([])}>Auswahl aufheben</Button>
        </Paper>
      )}

      {isMobile ? (
        <Box display="flex" flexDirection="column" gap={1.5}>
          {tickets.map((ticket) => (
            <Card key={ticket.id} onClick={() => navigate(`/tickets/${ticket.id}`)} sx={{ cursor: 'pointer', '&:active': { transform: 'scale(0.98)' }, transition: 'transform 0.1s' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                  <Box flex={1} minWidth={0}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }} noWrap>{ticket.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      #{ticket.id}{ticket.external_ticket_number && ` | BW: ${ticket.external_ticket_number}`}
                    </Typography>
                  </Box>
                  {isAdmin && (
                    <Box display="flex" alignItems="center" sx={{ ml: 1 }} onClick={(e) => e.stopPropagation()}>
                      <Checkbox size="small" checked={selected.includes(ticket.id)} onChange={() => toggleSelect(ticket.id)} sx={{ p: 0.5 }} />
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(ticket.id); }}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  <Chip label={getStatusLabel(ticket.status)} color={getStatusColor(ticket.status)} size="small" />
                  {ticket.kc_code && <Chip label={ticket.kc_code} size="small" variant="outlined" />}
                  {ticket.category_name && <Chip label={ticket.category_name} size="small" variant="outlined" />}
                  {ticket.attachment_count > 0 && <Chip icon={<AttachFile />} label={ticket.attachment_count} size="small" variant="outlined" />}
                </Box>
              </CardContent>
            </Card>
          ))}
          {tickets.length === 0 && (
            <Paper sx={{ p: 3, textAlign: 'center' }}><Typography color="textSecondary">Keine Tickets gefunden</Typography></Paper>
          )}
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                {isAdmin && (
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      size="small"
                      checked={allVisibleSelected}
                      indeterminate={selected.length > 0 && !allVisibleSelected}
                      onChange={toggleSelectAll}
                    />
                  </TableCell>
                )}
                <TableCell>ID</TableCell>
                <TableCell>BW-Ticket</TableCell>
                <TableCell>Titel</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>KC</TableCell>
                <TableCell>Kategorie</TableCell>
                {isAdmin && <TableCell>Zugewiesen an</TableCell>}
                {isAdmin && <TableCell>Aktionen</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets.map((ticket) => (
                <TableRow key={ticket.id} selected={selected.includes(ticket.id)} onClick={() => navigate(`/tickets/${ticket.id}`)} sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}>
                  {isAdmin && (
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox size="small" checked={selected.includes(ticket.id)} onChange={() => toggleSelect(ticket.id)} />
                    </TableCell>
                  )}
                  <TableCell>#{ticket.id}</TableCell>
                  <TableCell>{ticket.external_ticket_number ? <Chip label={ticket.external_ticket_number} size="small" variant="outlined" /> : '-'}</TableCell>
                  <TableCell>{ticket.title}</TableCell>
                  <TableCell><Chip label={getStatusLabel(ticket.status)} color={getStatusColor(ticket.status)} size="small" /></TableCell>
                  <TableCell>{ticket.kc_code || '-'}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {ticket.category_name || '-'}
                      {ticket.attachment_count > 0 && (
                        <Chip icon={<AttachFile />} label={ticket.attachment_count} size="small" variant="outlined" sx={{ ml: 1, cursor: 'pointer', '&:hover': { backgroundColor: 'primary.light', color: 'white' } }}
                          onClick={(e) => { e.stopPropagation(); navigate(`/tickets/${ticket.id}?tab=1`); }} />
                      )}
                    </Box>
                  </TableCell>
                  {isAdmin && (
                    <TableCell>
                      {ticket.assigned_to_name
                        ? <Chip label={ticket.assigned_to_name} size="small" color="info" variant="outlined" />
                        : <Chip label="—" size="small" variant="outlined" />}
                    </TableCell>
                  )}
                  {isAdmin && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <IconButton size="small" onClick={() => handleDelete(ticket.id)}><Delete /></IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {tickets.length === 0 && (<Box p={3} textAlign="center"><Typography color="textSecondary">Keine Tickets gefunden</Typography></Box>)}
        </TableContainer>
      )}

      {!loading && hasMore && (
        <div ref={observerTarget} style={{ height: '20px', margin: '10px 0' }} />
      )}

      {loading && (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      )}

      {!hasMore && tickets.length > 0 && (
        <Box p={2} textAlign="center">
          <Typography color="textSecondary">Alle Tickets geladen</Typography>
        </Box>
      )}
    </Container>
  );
};

export default Tickets;